import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Box,
  Tooltip,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
const EditUser = () => {
  const theme = useTheme();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState('');
const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

const [roleList, setRoleList] = useState([]);
const [employeeList, setEmployeeList] = useState([]);
const [studentList, setStudentList] = useState([]);
const [loading, setLoading] = useState(true);
const [updating, setUpdating] = useState(false);

const [formData, setFormData] = useState({
  roleId: '',
  employeeId: '',
  userName: '',
  password: '',
  confirmPassword: '',
  expiryDate: '',
});

  const navigate = useNavigate();
  const { UserId } = useParams();

  // Static data
  const staticUserData = [
    {
      UserId: 1,
      UserRoleName: 'Superadmin',
      EmployeeName: 'Adam',
      UserName: 'warals@technology.com',
      Password: 'User@123',
      ExpiryDate: '2026-06-03',
    },
    {
      UserId: 2,
      UserRoleName: 'Admin',
      EmployeeName: 'Sara Khan',
      UserName: 'admin@school.com',
      Password: 'Admin@123',
      ExpiryDate: '2026-09-15',
    },
    {
      UserId: 3,
      UserRoleName: 'Counsellor',
      EmployeeName: 'Ritika Sharma',
      UserName: 'counsellor@school.com',
      Password: 'Counsel@123',
      ExpiryDate: '2025-12-31',
    },
    {
      UserId: 4,
      UserRoleName: 'Teacher',
      EmployeeName: 'Mohit Verma',
      UserName: 'teacher1@school.com',
      Password: 'Teach@123',
      ExpiryDate: '2026-06-30',
    },
    {
      UserId: 5,
      UserRoleName: 'Student',
      EmployeeName: 'Nikita Gupta',
      UserName: 'student1@school.com',
      Password: 'Stud@123',
      ExpiryDate: '2026-04-30',
    },
    {
      UserId: 6,
      UserRoleName: 'Accountant',
      EmployeeName: 'Ravi Mehra',
      UserName: 'account@school.com',
      Password: 'Account@123',
      ExpiryDate: '2027-01-01',
    },
    {
      UserId: 7,
      UserRoleName: 'Receptionist',
      EmployeeName: 'Anita Joshi',
      UserName: 'reception@school.com',
      Password: 'Welcome@123',
      ExpiryDate: '2026-08-15',
    },
    {
      UserId: 8,
      UserRoleName: 'Librarian',
      EmployeeName: 'Neha Sinha',
      UserName: 'library@school.com',
      Password: 'Library@123',
      ExpiryDate: '2026-10-10',
    },
  ];

  // Derive whether the currently selected role is "student" (case-insensitive)
  const selectedRole = roleList.find((r) => String(r.id) === String(formData.roleId));
  const isStudentRole = selectedRole
    ? (selectedRole.name || selectedRole.userGroupName || '').toLowerCase() === 'student'
    : false;

const loadData = async () => {
  setLoading(true);
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    setSnackbarMessage('Authentication token missing');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    setLoading(false);
    return;
  }

  const headers = { 'Authorization': `Bearer ${accessToken}` };

  try {
    const [rolesRes, employeesRes, userRes, studentsRes] = await Promise.all([
      fetch(`${apiUrl}/api/staff/user-roles/`, { headers }),
      fetch(`${apiUrl}/api/staff/employees/`, { headers }),
      fetch(`${apiUrl}/api/staff/users/${UserId}/`, { headers }),
      fetch(`${apiUrl}/api/admission/admissions/`, { headers }),
    ]);

    let roles = [];
    if (rolesRes.ok) {
      roles = await rolesRes.json();
      setRoleList(roles);
    } else {
      setSnackbarMessage('Failed to load roles');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }

    if (employeesRes.ok) {
      setEmployeeList(await employeesRes.json());
    } else {
      setSnackbarMessage('Failed to load employees');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }

    if (studentsRes.ok) {
      setStudentList(await studentsRes.json());
    } else {
      setSnackbarMessage('Failed to load students');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }

    if (userRes.ok) {
      const user = await userRes.json();

      const roleId = user.role?.id || user.role || user.role_id || '';

      const loadedRole = roles.find((r) => String(r.id) === String(roleId));
      const loadedRoleIsStudent = loadedRole
        ? (loadedRole.name || loadedRole.userGroupName || '').toLowerCase() === 'student'
        : false;

      const employeeId = loadedRoleIsStudent
        ? (user.admission?.id || user.admission || '')
        : (user.employee?.id || user.employee || user.employee_id || user.staff || '');

      setFormData({
        roleId: String(roleId),
        employeeId: String(employeeId),
        userName: user.username || user.email || '',
        password: user.raw_password_store || '',
        confirmPassword: user.raw_password_store || '',
        expiryDate: user.expiry_date || user.expiry || '',
      });
    } else {
      setSnackbarMessage('Failed to load user details');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      navigate('/users');
    }
  } catch (err) {
    setSnackbarMessage('Error loading data');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    setLoading(false);
  }
}; 

useEffect(() => {
  if (UserId) loadData();
}, [UserId]);

  // Load students from admissions API when student role is selected
  const loadStudents = async (token) => {
    const accessToken = token || localStorage.getItem('accessToken');
    if (!accessToken) return;

    const headers = { 'Authorization': `Bearer ${accessToken}` };

    try {
      const res = await fetch(`${apiUrl}/api/admission/admissions/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStudentList(data);
      } else {
        setSnackbarMessage('Failed to load students');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (err) {
      setSnackbarMessage('Error loading students');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };
 
const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({ ...prev, [name]: value }));
};

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

const handleSave = async (e) => {
  e.preventDefault();

  if (!formData.roleId || !formData.employeeId || !formData.userName || !formData.password || !formData.confirmPassword || !formData.expiryDate) {
    setSnackbarMessage('Please fill all required fields!');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  if (formData.password !== formData.confirmPassword) {
    setPasswordMatchError(true);
    return;
  }

  setUpdating(true);
  const accessToken = localStorage.getItem('accessToken');
  const payload = {
    role: parseInt(formData.roleId),
    employee: parseInt(formData.employeeId),
    username: formData.userName,
    password: formData.password,
    confirm_password: formData.confirmPassword,
    expiry_date: formData.expiryDate,
  };

  try {
    const response = await fetch(`${apiUrl}/api/staff/users/${UserId}/`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      setShowSuccess(true);
    } else {
      const err = await response.json().catch(() => ({}));
      setSnackbarMessage(err.detail || err.message || 'Failed to update user');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  } catch (err) {
    setSnackbarMessage('Error updating user');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    setUpdating(false);
  }
};

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            User Details
          </Typography>
        </Box>

      {showSuccess ? (
  <Modal
    open={showSuccess}
    onClose={() => navigate(-1)}
    BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: 500 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: 24,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: theme.palette.primary.main,
          padding: '16px 24px',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
          User Updated Successfully ✅
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ padding: '32px 24px', textAlign: 'center' }}>
        <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.primary }}>
          The user details have been updated successfully.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(-1)}
          sx={{
            minWidth: 120,
            borderRadius: '10px',
            fontWeight: 'bold',
          }}
        >
          OK
        </Button>
      </Box>
    </Box>
  </Modal>
) : (
          <form onSubmit={handleSave}>
            {loading ? (
  <Typography align="center" sx={{ py: 4 }}>Loading user details...</Typography>
) : (
          <div>
          <FormControl disabled fullWidth sx={{ mb: 3 }}>
<InputLabel sx={{ 
    color: theme.palette.text.primary,
    fontWeight: 400,
    '&.Mui-focused': { 
      color: theme.palette.primary.main,
      fontWeight: 500 
    }
  }}>
    User Role
  </InputLabel>  <Select
    name="roleId"
    value={formData.roleId}
    label="User Role"
    onChange={handleChange}
  >
    {roleList.map((role) => (
      <MenuItem key={role.id} value={String(role.id)}>
        {role.name || role.userGroupName || 'Unknown'}
      </MenuItem>
    ))}
  </Select>
</FormControl>

{/* Employee Name dropdown — switches to Student Name when role is "student" */}
<FormControl disabled fullWidth sx={{ mb: 3 }}>
  <InputLabel sx={{ 
        color: theme.palette.text.primary,
        fontWeight: 400,
        '&.Mui-focused': { 
          color: theme.palette.primary.main,
          fontWeight: 500 
        }
      }}>
        {isStudentRole ? 'Student Name' : 'Employee Name'}
      </InputLabel>
        <Select
    name="employeeId"
    value={formData.employeeId}
    label={isStudentRole ? 'Student Name' : 'Employee Name'}
    onChange={handleChange}
  >
    {isStudentRole ? (
      studentList.length === 0 ? (
        <MenuItem disabled>No students available</MenuItem>
      ) : (
        studentList.map((student) => (
          <MenuItem key={student.id} value={String(student.id)}>
            {student.candidate_name || 'Unknown'}
          </MenuItem>
        ))
      )
    ) : (
      employeeList.map((emp) => (
        <MenuItem key={emp.id} value={String(emp.id)}>
          {emp.full_name || emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown'}
        </MenuItem>
      ))
    )}
  </Select>
</FormControl>
            <TextField
              label="User Name"
              variant="outlined"
              fullWidth
              name="userName"
              value={formData.userName}
              onChange={handleChange}
               sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={passwordMatchError}
              helperText={passwordMatchError ? "Passwords do not match" : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={passwordMatchError}
              helperText={passwordMatchError ? "Passwords do not match" : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
            <TextField
              label="Expiry Date"
              type="date"
              variant="outlined"
              fullWidth
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
             sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Tooltip
                title={
                  !formData.userRole ? 'Enter User Role' :
                  !formData.employeeName ? 'Enter Employee Name' :
                  !formData.userName ? 'Enter User Name' :
                  !formData.password ? 'Enter Password' :
                  !formData.confirmPassword ? 'Enter Confirm Password' :
                  !formData.expiryDate ? 'Enter Expiry Date' : ''
                }
                arrow
              >
               <Button
  variant="contained"
  type="submit"
  disabled={updating || !formData.roleId || !formData.employeeId || !formData.userName || !formData.password || !formData.confirmPassword || !formData.expiryDate}
>
  {updating ? 'Updating...' : 'UPDATE'}
</Button>
              </Tooltip>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </div>
)}
          </form>
        )}
      </Box>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
};

export default EditUser;